import type { ExperimentDef } from '../types';
import { runPredatorPrey } from './engine';

export const predatorPrey: ExperimentDef = {
  id: 'predator_prey',
  title: 'Predator and prey',
  summary: 'Watch two populations chase each other through the Lotka–Volterra cycle. Vary growth, predation, efficiency, death rate and the starting numbers.',
  seriesKeys: ['t', 'prey', 'predators'],
  seriesLabels: { t: 'Season', prey: 'Prey (animals)', predators: 'Predators (animals)' },
  params: [
    {
      kind: 'number',
      key: 'prey_growth',
      label: 'Prey growth rate',
      unit: 'per season',
      min: 0.1,
      max: 2,
      step: 0.05,
      default: 1,
      description: 'How fast prey multiply without predators, per season.',
    },
    {
      kind: 'number',
      key: 'predation',
      label: 'Predation rate',
      unit: 'per encounter',
      min: 0.01,
      max: 0.5,
      step: 0.005,
      default: 0.1,
      description: 'How often an encounter kills a prey animal.',
    },
    {
      kind: 'number',
      key: 'predator_efficiency',
      label: 'Predator efficiency',
      unit: 'per encounter',
      min: 0.01,
      max: 0.5,
      step: 0.005,
      default: 0.05,
      description: 'How much each kill helps predators reproduce.',
    },
    {
      kind: 'number',
      key: 'predator_death',
      label: 'Predator death rate',
      unit: 'per season',
      min: 0.1,
      max: 2,
      step: 0.05,
      default: 0.5,
      description: 'How fast predators die off without prey, per season.',
    },
    {
      kind: 'number',
      key: 'initial_prey',
      label: 'Starting prey',
      unit: 'animals',
      min: 1,
      max: 200,
      step: 1,
      default: 20,
      description: 'Prey population at the start.',
    },
    {
      kind: 'number',
      key: 'initial_predators',
      label: 'Starting predators',
      unit: 'animals',
      min: 1,
      max: 100,
      step: 1,
      default: 5,
      description: 'Predator population at the start.',
    },
    {
      kind: 'number',
      key: 'duration',
      label: 'Duration',
      unit: 'seasons',
      min: 10,
      max: 300,
      step: 5,
      default: 60,
      description: 'How many seasons to simulate.',
    },
  ],
  measurements: [
    { key: 'peak_prey', label: 'Peak prey', unit: 'animals', description: 'Largest prey population reached.' },
    { key: 'min_prey', label: 'Min prey', unit: 'animals', description: 'Smallest prey population reached.' },
    { key: 'peak_predators', label: 'Peak predators', unit: 'animals', description: 'Largest predator population reached.' },
    { key: 'min_predators', label: 'Min predators', unit: 'animals', description: 'Smallest predator population reached.' },
    { key: 'oscillation_period', label: 'Cycle length', unit: 'seasons', description: 'Average time between prey peaks. Empty if fewer than two peaks occurred.' },
    { key: 'mean_prey', label: 'Mean prey', unit: 'animals', description: 'Time-averaged prey population.' },
    { key: 'mean_predators', label: 'Mean predators', unit: 'animals', description: 'Time-averaged predator population.' },
  ],
  run: runPredatorPrey,
  noise: {
    peak_prey: { integer: true, relative: 0.03 },
    min_prey: { integer: true, relative: 0.03 },
    peak_predators: { integer: true, relative: 0.03 },
    min_predators: { integer: true, relative: 0.03 },
    oscillation_period: { resolution: 0.1, relative: 0.02 },
    mean_prey: { resolution: 0.1, relative: 0.02 },
    mean_predators: { resolution: 0.1, relative: 0.02 },
  },
  agentGuidance:
    'Populations cycle; predators peak after prey. Time averages equal the equilibrium: prey = death / efficiency, predators = growth / predation, whatever the start.',
};
